import { ref, readonly } from 'vue';
import { useStore } from 'vuex';
import StackzapCallsAPI from 'dashboard/api/channel/stackzap/stackzapCallsAPI';

const globalIsInitiating = ref(false);

// ---------------------------------------------------------------------------
// StackZap call session composable.
//
// StackZap calls use a WebSocket PCM bridge (not WebRTC):
// - Browser mic → getUserMedia → AudioWorklet → s16le PCM → WebSocket → StackZap
// - StackZap → WebSocket → s16le PCM → AudioContext → browser speakers
//
// The WebSocket endpoint is obtained from the inbox's StackZap config.
// ---------------------------------------------------------------------------

const globalStackzapSession = ref(null);
const globalMediaStream = ref(null);
const globalAudioContext = ref(null);
const globalWebSocket = ref(null);
const globalIsMicMuted = ref(false);

export function useStackzapCallSession() {
  const store = useStore();

  const getMediaWSEndpoint = (serverUrl, instanceId, callId) => {
    const base = serverUrl.replace(/^http/, 'ws');
    return `${base}/v1/instances/${instanceId}/call/media?call_id=${callId}&instance_id=${instanceId}`;
  };

  const initiateCall = async conversationId => {
    if (globalIsInitiating.value) return { status: 'LOCKED' };
    globalIsInitiating.value = true;
    try {
      const result = await StackzapCallsAPI.initiate(conversationId);
      return result;
    } finally {
      globalIsInitiating.value = false;
    }
  };

  const acceptIncomingCall = async ({ callId, callSid, inbox }) => {
    const result = await StackzapCallsAPI.accept(callId);

    // Connect audio after server accepts — use provider_call_id (callSid)
    // for the media WebSocket URL, not the internal DB callId.
    // NOTE: inbox in the store keeps snake_case keys from the API.
    const attrs = inbox.additional_attributes || inbox.additionalAttributes || {};
    const instanceId = attrs.stackzap_instance_id || attrs.stackzapInstanceId;
    const serverUrl = attrs.stackzap_server_url || attrs.stackzapServerUrl;
    const apiKey = attrs.stackzap_api_key || attrs.stackzapApiKey;
    const mediaCallId = callSid || callId;
    if (instanceId && serverUrl) {
      connectAudio(serverUrl, instanceId, mediaCallId, apiKey);
    }
    return result;
  };

  const rejectIncomingCall = async callId => {
    const result = await StackzapCallsAPI.reject(callId);
    disconnectAudio();
    return result;
  };

  const endActiveCall = async callId => {
    const result = await StackzapCallsAPI.terminate(callId);
    disconnectAudio();
    return result;
  };

  const connectAudio = async (serverUrl, instanceId, callId, apiKey) => {
    const token = apiKey || store.getters.getCurrentUserAccessToken;
    const wsUrl = `${getMediaWSEndpoint(serverUrl, instanceId, callId)}&token=${encodeURIComponent(token)}`;

    try {
      // Open WebSocket to StackZap media bridge
      const ws = new WebSocket(wsUrl);
      globalWebSocket.value = ws;

      // Set up microphone capture and playback
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
          channelCount: 1,
        },
      });
      globalMediaStream.value = stream;

      const audioCtx = new AudioContext({ sampleRate: 16000 });
      globalAudioContext.value = audioCtx;

      // Input: mic → ScriptProcessorNode → s16le PCM → WebSocket
      const source = audioCtx.createMediaStreamSource(stream);
      const processor = audioCtx.createScriptProcessor(4096, 1, 1);
      source.connect(processor);
      processor.connect(audioCtx.destination);

      processor.onaudioprocess = event => {
        if (ws.readyState !== WebSocket.OPEN) return;
        const input = event.inputBuffer.getChannelData(0);
        const pcm = float32ToS16LE(input);
        ws.send(pcm);
      };

      // Output: WebSocket → s16le PCM → AudioBuffer → speakers
      ws.binaryType = 'arraybuffer';
      ws.onmessage = event => {
        const pcm = new Int16Array(event.data);
        const float32 = s16LEToFloat32(pcm);
        const buf = audioCtx.createBuffer(1, float32.length, 16000);
        buf.getChannelData(0).set(float32);
        const bufSrc = audioCtx.createBufferSource();
        bufSrc.buffer = buf;
        bufSrc.connect(audioCtx.destination);
        bufSrc.start();
      };

      ws.onclose = () => disconnectAudio();
      ws.onerror = () => disconnectAudio();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[STACKZAP] Audio connection error:', err);
      disconnectAudio();
    }
  };

  const disconnectAudio = () => {
    if (globalWebSocket.value) {
      globalWebSocket.value.close();
      globalWebSocket.value = null;
    }
    if (globalMediaStream.value) {
      globalMediaStream.value.getTracks().forEach(t => t.stop());
      globalMediaStream.value = null;
    }
    if (globalAudioContext.value) {
      globalAudioContext.value.close();
      globalAudioContext.value = null;
    }
    globalIsMicMuted.value = false;
  };

  const toggleMic = () => {
    const stream = globalMediaStream.value;
    if (!stream) return;
    globalIsMicMuted.value = !globalIsMicMuted.value;
    stream.getAudioTracks().forEach(t => {
      t.enabled = !globalIsMicMuted.value;
    });
  };

  const isInCall = () => globalWebSocket.value !== null;

  return {
    initiateCall,
    acceptIncomingCall,
    rejectIncomingCall,
    endActiveCall,
    connectAudio,
    disconnectAudio,
    toggleMic,
    isInCall,
    isInitiating: readonly(globalIsInitiating),
    isMicMuted: readonly(globalIsMicMuted),
  };
}

// Cleanup helper for page unload
export function sendStackzapTerminateBeacon() {
  // Terminate is best-effort on unload via navigator.sendBeacon
  if (globalWebSocket.value) {
    globalWebSocket.value.close();
  }
  if (globalMediaStream.value) {
    globalMediaStream.value.getTracks().forEach(t => t.stop());
  }
}

export function cleanupStackzapSession() {
  if (globalMediaStream.value) {
    globalMediaStream.value.getTracks().forEach(t => t.stop());
    globalMediaStream.value = null;
  }
  if (globalAudioContext.value) {
    globalAudioContext.value.close();
    globalAudioContext.value = null;
  }
  if (globalWebSocket.value) {
    globalWebSocket.value.close();
    globalWebSocket.value = null;
  }
  globalIsMicMuted.value = false;
}

// PCM conversion helpers (shared with the WhatsApp WebRTC composable)
function float32ToS16LE(float32) {
  const len = float32.length;
  const buf = new ArrayBuffer(len * 2);
  const view = new DataView(buf);
  for (let i = 0; i < len; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buf;
}

function s16LEToFloat32(int16) {
  const len = int16.length;
  const float32 = new Float32Array(len);
  for (let i = 0; i < len; i++) {
    float32[i] = int16[i] / 32768.0;
  }
  return float32;
}
